/**
 * @file Sidebar.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Friday, 29th January 2021 6:32:18 am
 * @copyright 2015 - 2021 SKAT LLC, Delive LLC
 * @flow
 */
import type { Record, Arrow } from '../types'

import cn from 'classnames'
import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList, faComments } from '@fortawesome/free-solid-svg-icons'
import { library } from '@fortawesome/fontawesome-svg-core'
import { mix } from 'mixwith'
import mList from '../utils/mList'
import sortBy from 'lodash/sortBy'
import { isMobileMode, genTestName } from 'web-panel/globals'

library.add(faList, faComments)

const KEY = {
  UP: 38,
  DOWN: 40,
  ENTER: 13
}

type Props = {
  onActivate: ?Function
}

// $FlowFixMe[signature-verification-failure]
class SideBar extends mix(React.Component).with(mList) {
  constructor (props: Props) {
    super(props)
    this.state = {
      focused: false,
      active: 0,
      buttons: this.defineList('buttons')
    }

    this.onActivate = this.props.onActivate || (() => {})
  }

  /**
   * Прежде чем установить активный элемент сбрасываем фокус,
   * потом при навигации кнопками мы начинаем движение фокуса
   * от активного элемента
   */
  activate : Arrow = ({ id }: Record) : Promise<void> => {
    return new Promise((resolve) => {
      const buttons = this.state.buttons

      // Bluring focused element if exist
      let index = buttons.findIndex(i => i.isFocused)
      if (buttons[index]) buttons[index].isFocused = false

      // Activating by name
      index = buttons.findIndex((i) => i.id === id)
      buttons[index].isHighlighed = false

      this.setState({ buttons, active: index }, () => {
        this.onActivate(buttons[index])
        resolve()
      })

      if (isMobileMode()) {
        try {
          const element = document.getElementById('collapse')
          if (element) (element:any).checked = 'checked'
        } catch (ignored) {}
      }
    })
  }

  highlight : Arrow = async ({ id }: Record) => {
    await this.updateButtons({ id, isHighlighed: true })
  }

  handleFocus : Arrow = () => {
    this.setState({ focused: true })
  }

  handleUnFocus : Arrow = () => {
    this.setState({ focused: false })
  }

  /**
   * После добавления кнопок, нужно их отсортировать
   * см. mList чтобы узнать про этот метод подробнее
   */
  _postAddButtons (buttons: Array<Record>) {
    this.setState({ buttons: sortBy(this.state.buttons, 'priority') })
  }

  /**
   * Сдесь реализуется переключение фокуса стрелками на клавиатуре
   * и активация свокусированной кнопки при нажатии на Enter
   * @param {*} event событие
   */
  handleKeyboard : Arrow = (e: any) => {
    const { buttons, active } = this.state
    let index = buttons.findIndex(i => i.isFocused)
    if (!buttons[index]) index = active

    if (e.keyCode === KEY.DOWN || e.keyCode === KEY.UP) {
      buttons[index].isFocused = false

      if (e.keyCode === KEY.DOWN) index++
      if (e.keyCode === KEY.UP) index--

      if (index > buttons.length - 1) index = 0
      if (index < 0) index = buttons.length - 1
      buttons[index].isFocused = true
      this.setState({ buttons })
    }

    if (e.keyCode === KEY.ENTER) this.activate({ id: buttons[index].id })
  }

  render () : React.Node {
    const { buttons, active } = this.state

    return (
      <div tabIndex={1} onKeyDown={this.handleKeyboard} onFocus={this.handleFocus} onBlur={this.handleUnFocus} className='sidebar-buttons'>{
        buttons.map(({ id, testName, name, label, title, icon, isDisabled, isPending, isFocused, isHighlighed }, index) => {
          const className = cn('sidebar-button', {
            active: index === active,
            disabled: isDisabled,
            spinner: isPending,
            focused: isFocused,
            highlighted: isHighlighed
          })
          return (
            <div {...genTestName(testName)} key={name} title={title} className={className} onClick={() => this.activate({ id })}>
              <div className='inner'>
                <FontAwesomeIcon className='icon' icon={icon} />
                <span>{label}</span>
              </div>
            </div>
          )
        })
      }
      </div>
    )
  }
}

export default SideBar
